import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
    FindAllPowerStationsBasicReactQuery,
    FindAllTeleObjectsConfigsByEquipmentId,
    PowerStationBasic
} from "../PowerStation/PowerStationReactQuery";
import {FindAllEquipmentsByPowerStationIdReactQuery} from "../Equipment/EquipmentReactQuery";
import {Equipment, TeleObjectConfig} from "../NotificationGroup/NotificationGroupFormPage";
import {RowTeleObjectData} from "../../../Shared/Types/TeleObject";
import {SaveTeleObjectReactQuery, TeleObjectToSave} from "./TeleObjectReactQuery";

interface Props {
    open: boolean;
    teleObject: RowTeleObjectData | null;
    onClose: () => void;
    onSaved: () => void;
}


const DEFAULT_STATUS = "10";

const MODAL_PADDING = "24px";

const REQUIRED_FIELD_LABEL = {
    powerStation: "Usina",
    equipment: "Equipamento",
    teleObjectConfig: "Teleobjeto"
} as const;

const formatFieldList = (fields: Array<string>): string =>
    fields.length === 1
        ? fields[0]
        : `${fields.slice(0, -1).join(", ")} e ${fields[fields.length - 1]}`;

export const TeleObjectFormModal = ({open, teleObject, onClose, onSaved}: Props) => {
    const isEditing = teleObject?.teleObjectId != null;

    const [powerStationId, setPowerStationId] = useState<number | null>(null);
    const [equipmentId, setEquipmentId] = useState<number | null>(null);
    const [teleObjectConfigId, setTeleObjectConfigId] = useState<number | null>(null);

    const [pendingFields, setPendingFields] = useState<Array<string>>([]);
    const [formError, setFormError] = useState<string | null>(null);

    const {
        data: powerStationsData,
        isLoading: isLoadingPowerStations,
        isError: isPowerStationsError
    } = FindAllPowerStationsBasicReactQuery();
    const {
        data: equipmentsData,
        isFetching: isFetchingEquipments,
        isError: isEquipmentsError
    } = FindAllEquipmentsByPowerStationIdReactQuery(powerStationId);
    const {
        data: teleObjectsConfigData,
        isFetching: isFetchingTeleObjectsConfig,
        isError: isTeleObjectsConfigError
    } = FindAllTeleObjectsConfigsByEquipmentId(equipmentId ?? undefined);

    const hasLoadError = isPowerStationsError || isEquipmentsError || isTeleObjectsConfigError;

    const mutationSave = SaveTeleObjectReactQuery();

    const powerStations: PowerStationBasic[] = useMemo(
        () => [...(powerStationsData ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
        [powerStationsData]
    );

    const equipments: Equipment[] = useMemo(
        () => [...(equipmentsData?.data ?? [])].sort((a: Equipment, b: Equipment) => a.name.localeCompare(b.name)),
        [equipmentsData]
    );

    const teleObjectsConfig: TeleObjectConfig[] = useMemo(
        () => [...(teleObjectsConfigData ?? [])].sort((a: TeleObjectConfig, b: TeleObjectConfig) => a.name.localeCompare(b.name)),
        [teleObjectsConfigData]
    );

    useEffect(() => {
        if (!open) return;

        setPowerStationId(teleObject?.powerStationId ?? null);
        setEquipmentId(teleObject?.equipmentId ?? null);
        setTeleObjectConfigId(teleObject?.teleObjectConfigId ?? null);
        setPendingFields([]);
        setFormError(null);
    }, [open, teleObject]);

    const selectedPowerStation = powerStations.find(powerStation => powerStation.id === powerStationId) ?? null;
    const selectedEquipment = equipments.find(equipment => equipment.id === equipmentId) ?? null;
    const selectedTeleObjectConfig = teleObjectsConfig.find(config => config.id === teleObjectConfigId) ?? null;

    const teleObjectPoint = selectedTeleObjectConfig?.point ?? teleObject?.teleObjectConfigPoint ?? null;

    const specificationTypeId =
        selectedTeleObjectConfig?.teleObjectSpecificationType?.id ?? teleObject?.teleObjectSpecificationTypeId ?? null;

    const clearPendingField = useCallback((label: string) => {
        setPendingFields(pending => pending.filter(field => field !== label));
        setFormError(null);
    }, []);

    const handlePowerStationChange = (newValue: PowerStationBasic | null) => {
        setPowerStationId(newValue?.id ?? null);
        setEquipmentId(null);
        setTeleObjectConfigId(null);
        clearPendingField(REQUIRED_FIELD_LABEL.powerStation);
    };

    const handleEquipmentChange = (newValue: Equipment | null) => {
        setEquipmentId(newValue?.id ?? null);
        setTeleObjectConfigId(null);
        clearPendingField(REQUIRED_FIELD_LABEL.equipment);
    };

    const handleTeleObjectConfigChange = (newValue: TeleObjectConfig | null) => {
        setTeleObjectConfigId(newValue?.id ?? null);
        clearPendingField(REQUIRED_FIELD_LABEL.teleObjectConfig);
    };

    const handleSave = () => {
        if (mutationSave.isLoading) return;

        const missingFields: Array<string> = [];
        if (powerStationId === null) missingFields.push(REQUIRED_FIELD_LABEL.powerStation);
        if (equipmentId === null) missingFields.push(REQUIRED_FIELD_LABEL.equipment);
        if (teleObjectConfigId === null) missingFields.push(REQUIRED_FIELD_LABEL.teleObjectConfig);

        if (missingFields.length > 0) {
            setPendingFields(missingFields);
            setFormError(`Para concluir o cadastro, selecione: ${formatFieldList(missingFields)}.`);
            return;
        }

        setPendingFields([]);

        if (specificationTypeId === null) {
            setFormError("O teleobjeto selecionado não possui tipo específico configurado. Escolha outro teleobjeto ou ajuste a configuração.");
            return;
        }

        if (equipmentId === null || teleObjectConfigId === null) return;

        setFormError(null);

        const objectToSave: TeleObjectToSave = {
            id: teleObject?.teleObjectId ?? null,
            status: DEFAULT_STATUS,
            equipment: {
                id: equipmentId,
                ...(selectedEquipment?.ets != null ? {ets: selectedEquipment.ets} : {})
            },
            teleObjectConfig: {id: teleObjectConfigId},
            teleObjectPosition: teleObjectPoint,
            teleObjectSpecificationType: {id: specificationTypeId}
        };

        mutationSave.mutate(objectToSave, {
            onSuccess: () => {
                onSaved();
            }
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            aria-label="tele-object-form-modal"
            PaperProps={{sx: {borderRadius: "12px"}}}
        >
            <DialogTitle sx={{padding: MODAL_PADDING, paddingBottom: "16px"}}>
                {isEditing ? "Editar Teleobjeto" : "Novo Teleobjeto"}
            </DialogTitle>
            {/* O padding vertical do conteúdo é zero: os 24px de respiro vêm do título acima
                e do DialogActions abaixo, e os 8px do primeiro campo evitam que o label
                flutuante seja cortado quando o conteúdo rola. */}
            <DialogContent sx={{paddingX: MODAL_PADDING, paddingY: 0}}>
                {hasLoadError &&
                    <Alert severity="error" sx={{marginTop: 1}}>
                        Não foi possível carregar as opções do formulário. Tente novamente.
                    </Alert>
                }

                {formError &&
                    <Alert severity="error" sx={{marginTop: 1}}>
                        {formError}
                    </Alert>
                }

                <Autocomplete
                    fullWidth
                    id="autocomplete-power-station"
                    sx={{marginTop: 1}}
                    options={powerStations}
                    loading={isLoadingPowerStations}
                    loadingText="Carregando usinas..."
                    noOptionsText="Nenhuma usina disponível"
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedPowerStation}
                    onChange={(event, newValue) => handlePowerStationChange(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Usina"
                            variant="outlined"
                            placeholder="Selecione a Usina"
                            error={pendingFields.includes(REQUIRED_FIELD_LABEL.powerStation)}
                        />
                    )}
                />

                <Autocomplete
                    fullWidth
                    id="autocomplete-equipment"
                    sx={{marginTop: 2}}
                    options={equipments}
                    loading={isFetchingEquipments}
                    loadingText="Carregando equipamentos..."
                    noOptionsText="Nenhum equipamento para a usina selecionada"
                    disabled={powerStationId === null}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedEquipment}
                    onChange={(event, newValue) => handleEquipmentChange(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Equipamento"
                            variant="outlined"
                            placeholder="Selecione o Equipamento"
                            error={pendingFields.includes(REQUIRED_FIELD_LABEL.equipment)}
                        />
                    )}
                />

                <Autocomplete
                    fullWidth
                    id="autocomplete-tele-object-config"
                    sx={{marginTop: 2}}
                    options={teleObjectsConfig}
                    loading={isFetchingTeleObjectsConfig}
                    loadingText="Carregando teleobjetos..."
                    noOptionsText="Nenhum teleobjeto para o equipamento selecionado"
                    disabled={equipmentId === null}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedTeleObjectConfig}
                    onChange={(event, newValue) => handleTeleObjectConfigChange(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Teleobjeto"
                            variant="outlined"
                            placeholder="Selecione o Teleobjeto"
                            error={pendingFields.includes(REQUIRED_FIELD_LABEL.teleObjectConfig)}
                        />
                    )}
                />

                <TextField
                    sx={{marginTop: 2}}
                    label="Ponto DNP3 (Endereço do TO)"
                    value={teleObjectPoint ?? ''}
                    fullWidth
                    disabled
                    InputProps={{readOnly: true}}
                />
            </DialogContent>
            <DialogActions sx={{padding: MODAL_PADDING}}>
                <Button onClick={onClose}
                        color="primary"
                        variant="outlined"
                        size={"small"}
                >
                    Cancelar
                </Button>
                <Button onClick={handleSave}
                        color="primary"
                        variant="contained"
                        size={"small"}
                >
                    {isEditing ? "Salvar" : "Criar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
